/**
 * filePaymentsColumns — Column definitions and preferences for the bulk payments table.
 */
import Badge from '@cloudscape-design/components/badge';
import Link from '@cloudscape-design/components/link';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import { PaymentResponse } from 'src/store/slices/payment/paymentState';
import { formatIsoTimestamp, formatLatency } from 'src/utils/formatters';

/** Maps a beacon payment state to a Cloudscape StatusIndicator type. */
const stateToIndicatorType = (state: string): 'success' | 'error' | 'in-progress' => {
  if (state === 'SUCCESS') return 'success';
  if (state === 'FAILED') return 'error';
  return 'in-progress';
};

/** Table column definitions for payment results. */
export const PAYMENT_COLUMN_DEFINITIONS = [
  {
    id: 'id',
    header: 'Payment ID',
    cell: (item: PaymentResponse) => (
      <Link href={`/payment?id=${encodeURIComponent(item.id)}`} external>
        {item.id}
      </Link>
    ),
    sortingField: 'id',
  },
  {
    id: 'beaconPaymentState',
    header: 'State',
    cell: (item: PaymentResponse) => (
      <StatusIndicator type={stateToIndicatorType(item.beaconPaymentState)}>
        {item.beaconPaymentState}
      </StatusIndicator>
    ),
  },
  {
    id: 'beaconPaymentStatus',
    header: 'Status',
    cell: (item: PaymentResponse) => item.beaconPaymentStatus || '—',
  },
  {
    id: 'paymentType',
    header: 'Type',
    cell: (item: PaymentResponse) => item.paymentType ? <Badge>{item.paymentType}</Badge> : '—',
  },
  {
    id: 'pluginName',
    header: 'Plugin',
    cell: (item: PaymentResponse) => item.pluginName || '—',
  },
  {
    id: 'totalLatencyMs',
    header: 'Total Latency',
    cell: (item: PaymentResponse) => formatLatency(item.totalLatencyMs),
  },
  {
    id: 'createdAt',
    header: 'Created',
    cell: (item: PaymentResponse) => formatIsoTimestamp(item.createdAt),
  },
];

/** Options for the visible content preferences. */
export const PAYMENT_VISIBLE_CONTENT_OPTIONS = PAYMENT_COLUMN_DEFINITIONS.map(col => ({
  id: col.id,
  label: col.header,
}));

/** Page size options for CollectionPreferences. */
export const PAGE_SIZE_OPTIONS = [
  { value: 10, label: '10 payments' },
  { value: 25, label: '25 payments' },
  { value: 50, label: '50 payments' },
  { value: 100, label: '100 payments' },
];

/** Default columns visible on initial load. */
export const DEFAULT_VISIBLE_COLUMNS = PAYMENT_COLUMN_DEFINITIONS.map(col => col.id);

export const DEFAULT_PAGE_SIZE = 25;
